// src/calculators/labor.js — четирите панела на Фаза 4: „Полагаем отпуск",
// „Давност на отпуска", „Извънреден труд" и „Имуществена отговорност".
// Нормативната сметка е в services/{leave,overtime,liability}.js — обща за
// тестове и екран (моделът на severance/seniority).

import { el, clear, field, input, row } from '../shared/dom.js';
import { money } from '../shared/format.js';
import { dateInput, localTodayIso } from '../shared/dates.js';
import {
  proportionalLeave, monthsOfService, firstLeaveEligibleDate,
  leaveStatus, MIN_ANNUAL_LEAVE_DAYS,
} from '../../services/leave.js';
import { hourlyRate, overtimePay, overtimeLimitsCheck, UPLIFT } from '../../services/overtime.js';
import { liabilityLimit, garnishablePart, orderDeadline, minWageAt } from '../../services/liability.js';
// Правното основание (задача 6/6) — цитатите са сверени в lex.bg.
import { withLaw, hintify } from './law.js';

const okIso = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s || '');
const bg = (iso) => String(iso || '').split('-').reverse().join('.');
const line = (label, value, big) => el('div', { class: 'calc-line' + (big ? ' big' : '') }, el('span', { text: label }), el('span', { text: value }));
const noteRow = (t) => el('div', { style: 'font-size:12px;color:var(--muted);padding:2px 0', text: '• ' + t });

// ---- В1. Полагаем платен годишен отпуск -------------------------------------

export function buildAnnualLeaveCalc() {
  const f = {
    annual: input({ type: 'number', min: '1', value: String(MIN_ANNUAL_LEAVE_DAYS) }),
    extra: input({ type: 'number', min: '0', value: '0', placeholder: 'напр. 5 за ненормиран ден' }),
    from: dateInput(''),
    to: dateInput(''),
    firstJob: el('input', { type: 'checkbox' }),
    proRata: el('input', { type: 'checkbox' }),
    factor: input({ type: 'number', step: '0.05', min: '0.05', max: '1', value: '0.5' }),
  };
  const result = el('div', { class: 'calc-result' });
  const notesBox = el('div', { style: 'margin-top:8px' });
  const factorField = field('Пропорция (напр. 0,5 при 4 от 8 часа)', f.factor, { size: 's' });

  function recompute() {
    clear(result); clear(notesBox);
    factorField.style.display = f.proRata.checked ? '' : 'none';
    const from = f.from.dataset.iso, to = f.to.dataset.iso;
    if (!okIso(from) || !okIso(to) || from > to) {
      result.appendChild(el('div', { class: 'calc-line', text: 'Попълнете периода на стажа във фирмата (от – до).' }));
      return;
    }
    const annual = (Number(f.annual.value) || 0) + (Number(f.extra.value) || 0);
    let months, r;
    try {
      months = monthsOfService({ from, to });
      r = proportionalLeave({ annualDays: annual, months, proRataFactor: f.proRata.checked ? Number(f.factor.value) : undefined });
    } catch (e) {
      result.appendChild(el('div', { class: 'calc-hint-line', text: '⚠ ' + e.message }));
      return;
    }
    result.appendChild(line('Признати месеци (НРВПО чл. 42)', String(months)));
    result.appendChild(line('Полагаем отпуск', r.exactDays.toFixed(2).replace('.', ',') + ' раб. дни', true));
    if (r.note) notesBox.appendChild(noteRow(r.note));
    if (f.firstJob.checked) {
      notesBox.appendChild(noteRow('Първи трудов стаж: право на ПОЛЗВАНЕ от ' + bg(firstLeaveEligibleDate(from)) + ' (4 месеца — чл. 155, ал. 2 КТ). Правото на отпуск се трупа и преди това.'));
    }
    notesBox.appendChild(noteRow('Основният размер е не по-малко от ' + MIN_ANNUAL_LEAVE_DAYS + ' раб. дни (чл. 155, ал. 4); ненормиран ден/специфични условия дават още поне 5 (чл. 156).'));
  }
  Object.values(f).forEach((i) => { i.addEventListener('input', recompute); i.addEventListener('change', recompute); });
  recompute();

  return withLaw(el('div', { class: 'panel calc-panel' },
    el('h3', { class: 'calc-title', text: 'Полагаем платен годишен отпуск (чл. 155–156а КТ)' }),
    row(hintify(field('Годишен размер (раб. дни)', f.annual, { size: 's' }),
      'чл. 155, ал. 4 КТ', 'Основният платен годишен отпуск е не по-малко от 20 работни дни.'),
      field('Допълнителен отпуск (раб. дни)', f.extra, { size: 's' })),
    row(field('Стаж във фирмата от', f.from, { size: 's' }), field('до (вкл.)', f.to, { size: 's' })),
    el('div', { class: 'field' }, el('label', { class: 'choice' }, f.firstJob, el('span', { text: 'Постъпва на работа за ПЪРВИ път (чл. 155, ал. 2)' }))),
    el('div', { class: 'field' }, el('label', { class: 'choice' }, f.proRata, el('span', { text: 'Приложи пропорция за непълно работно време (по вътрешни правила)' }))),
    factorField,
    result,
    notesBox), {
    when: [
      'Служител постъпва или напуска през годината — полагаемият отпуск е пропорционален на месеците.',
      'Първо постъпване на работа: ползване е възможно след 4 месеца стаж, но правото се трупа от първия ден.',
      'Сверка на остатъците в края на годината преди прехвърляне/погасяване.',
      'Резултатът може да се запише в досието на лицето.',
    ],
    quotes: [
      { ref: 'чл. 155, ал. 1, 2 и 4 КТ (изм. ДВ, бр. 107/2020)', text: 'Всеки работник или служител има право на платен годишен отпуск. При постъпване на работа за първи път работникът или служителят може да ползва платения си годишен отпуск, когато придобие най-малко 4 месеца трудов стаж. … Размерът на основния платен годишен отпуск е не по-малко от 20 работни дни.' },
      { ref: 'чл. 155, ал. 3 КТ (нова — ДВ, бр. 52/2004)', text: 'При прекратяване на трудовото правоотношение преди придобиване на 4 месеца трудов стаж работникът или служителят има право на обезщетение за неползван платен годишен отпуск, изчислено по реда на чл. 224, ал. 1.' },
    ],
  });
}

// ---- В2. Давност на отпуска -------------------------------------------------

export function buildLeaveExpiryCalc() {
  const rows = [{ year: String(new Date().getFullYear() - 1), days: '' }];
  const asOfI = dateInput(localTodayIso());
  const rowsWrap = el('div');
  const result = el('div', { class: 'calc-result' });
  const notesBox = el('div', { style: 'margin-top:8px' });

  function recompute() {
    clear(result); clear(notesBox);
    const balances = rows
      .filter((r) => Number(r.year) >= 1990 && Number(r.days) > 0)
      .map((r) => ({ year: Number(r.year), days: Number(r.days) }));
    if (!balances.length || !okIso(asOfI.dataset.iso)) {
      result.appendChild(el('div', { class: 'calc-line', text: 'Попълнете остатъците по години.' }));
      return;
    }
    const status = leaveStatus({ balances, asOf: asOfI.dataset.iso });
    status.forEach((s) => {
      const state = s.expired ? '✖ ПОГАСЕН на ' + bg(s.expiresOn)
        : (s.warnSoon ? '⚠ изтича на ' + bg(s.expiresOn) + ' — планирайте до декември' : 'до ' + bg(s.expiresOn));
      result.appendChild(line('Отпуск за ' + s.year + ' г. — ' + s.days + ' дни', state, s.warnSoon && !s.expired));
    });
    notesBox.appendChild(noteRow('Работодателят дължи осигурено ползване до 6 месеца от края на годината (чл. 176, ал. 2); иначе работникът сам определя времето с 14-дневно писмено уведомление (ал. 3).'));
    notesBox.appendChild(noteRow('По практиката на Съда на ЕС (Max-Planck) автоматичното погасяване е спорно, ако работодателят не е поканил изрично работника — датите са ориентир.'));
    notesBox.appendChild(noteRow('Отложен по чл. 176, ал. 1 отпуск (до 10 раб. дни) се погасява 2 г. от края на годината, в която е отпаднала причината — отбележете такива случаи отделно.'));
  }

  function drawRows() {
    clear(rowsWrap);
    rows.forEach((r, i) => {
      const yearI = input({ type: 'number', min: '1990', max: '2200', value: r.year, style: 'max-width:100px' });
      yearI.addEventListener('input', () => { r.year = yearI.value; recompute(); });
      const daysI = input({ type: 'number', min: '0', step: '0.5', value: r.days, placeholder: 'дни', style: 'max-width:90px' });
      daysI.addEventListener('input', () => { r.days = daysI.value; recompute(); });
      rowsWrap.appendChild(el('div', { class: 'field-row', style: 'align-items:flex-end' },
        field('Година', yearI, { size: 's' }),
        field('Остатък (раб. дни)', daysI, { size: 's' }),
        el('button', { class: 'btn sm danger', type: 'button', 'aria-label': 'Махни реда', text: '×', style: 'margin-bottom:2px', onClick: () => { rows.splice(i, 1); if (!rows.length) rows.push({ year: '', days: '' }); drawRows(); recompute(); } })));
    });
    rowsWrap.appendChild(el('button', { class: 'btn sm ghost', type: 'button', text: '+ Добави година', onClick: () => { rows.push({ year: '', days: '' }); drawRows(); } }));
  }
  drawRows();
  asOfI.addEventListener('input', recompute);
  recompute();

  return withLaw(el('div', { class: 'panel calc-panel' },
    el('h3', { class: 'calc-title', text: 'Давност на отпуска (чл. 176 и 176а КТ)' }),
    el('p', { style: 'font-size:13px;color:var(--muted);margin:0 0 10px', text: 'Правото се погасява 2 години от края на годината, за която се полага отпускът. Въведете остатъците по години и вижте кой кога „изгаря".' }),
    rowsWrap,
    hintify(field('Към дата', asOfI, { size: 's' }),
      'чл. 176а, ал. 1 КТ', 'Правото на ползване се погасява по давност 2 години от края на годината, за която се полага отпускът.'),
    result,
    notesBox), {
    when: [
      'Годишна инвентаризация на неползваните отпуски — кой остатък кога изгаря.',
      'Служител с остатъци от няколко години иска да знае какво още може да ползва.',
      'Отложен отпуск по чл. 176, ал. 1 — давността тече от края на годината, в която е отпаднала причината.',
      'Преди прекратяване: погасеният по давност отпуск не влиза в обезщетението по чл. 224.',
    ],
    quotes: [
      { ref: 'чл. 176а, ал. 1 КТ (нов — ДВ, бр. 18/2011)', text: 'Когато платеният годишен отпуск или част от него не е ползван до изтичане на две години от края на годината, за която се полага, независимо от причините за това, правото на ползването му се погасява по давност.' },
      { ref: 'чл. 176а, ал. 2 КТ (изм. ДВ, бр. 54/2015)', text: 'Когато платеният годишен отпуск е отложен при условията и по реда на чл. 176, ал. 1, правото на работника или служителя на ползването му се погасява по давност след изтичане на две години от края на годината, в която е отпаднала причината за неползването му.' },
    ],
  });
}

// ---- Г1. Извънреден труд ----------------------------------------------------

export function buildOvertimeCalc() {
  const f = {
    salary: input({ type: 'number', step: '0.01', min: '0', value: '', placeholder: 'напр. 1008' }),
    year: input({ type: 'number', min: '2000', max: '2200', value: String(new Date().getFullYear()), style: 'max-width:100px' }),
    month: el('select', {}, ...Array.from({ length: 12 }, (_, i) => el('option', { value: i + 1, text: String(i + 1).padStart(2, '0'), selected: i === new Date().getMonth() }))),
    hours: input({ type: 'number', step: '0.5', min: '0', value: '', placeholder: 'напр. 6' }),
    dayType: el('select', {},
      el('option', { value: 'workday', text: 'Работен ден (+' + UPLIFT.workday + '%)' }),
      el('option', { value: 'weekend', text: 'Почивен ден (+' + UPLIFT.weekend + '%)' }),
      el('option', { value: 'holiday', text: 'Официален празник (+' + UPLIFT.holiday + '%)' }),
      el('option', { value: 'sirv', text: 'При СИРВ — над нормата (+' + UPLIFT.sirv + '%)' })),
    yearHours: input({ type: 'number', min: '0', value: '', placeholder: 'общо за годината (по избор)' }),
    hasKtd: el('input', { type: 'checkbox' }),
  };
  const result = el('div', { class: 'calc-result' });
  const warnBox = el('div', { style: 'margin-top:8px' });

  function recompute() {
    clear(result); clear(warnBox);
    const salary = Number(f.salary.value);
    if (!(salary > 0)) { result.appendChild(el('div', { class: 'calc-line', text: 'Въведете месечна заплата.' })); return; }
    let hr;
    try { hr = hourlyRate({ monthlySalary: salary, year: Number(f.year.value), month: Number(f.month.value) }); }
    catch (e) { result.appendChild(el('div', { class: 'calc-hint-line', text: '⚠ ' + e.message })); return; }
    result.appendChild(line('Работни дни в месеца', String(hr.workDays) + ' (' + hr.hours + ' ч.)'));
    result.appendChild(line('Часова ставка', money(Math.round(hr.rate * 100) / 100, 'EUR') + '/ч.'));
    const hours = Number(f.hours.value);
    if (hours > 0) {
      const pay = overtimePay({ hours, rate: hr.rate, dayType: f.dayType.value });
      result.appendChild(line('Основа за ' + hours + ' ч.', money(pay.base, 'EUR')));
      result.appendChild(line('Увеличение +' + pay.upliftPercent + '%', money(pay.uplift, 'EUR')));
      result.appendChild(line('Общо за извънредния труд', money(pay.total, 'EUR'), true));
    }
    const checks = overtimeLimitsCheck({ yearHours: f.yearHours.value === '' ? null : Number(f.yearHours.value), hasKtd: f.hasKtd.checked });
    checks.warnings.forEach((w) => warnBox.appendChild(el('div', { class: 'calc-hint-line', text: '⚠ ' + w })));
  }
  Object.values(f).forEach((i) => { i.addEventListener('input', recompute); i.addEventListener('change', recompute); });
  recompute();

  return withLaw(el('div', { class: 'panel calc-panel' },
    el('h3', { class: 'calc-title', text: 'Извънреден труд (чл. 262 КТ)' }),
    row(field('Месечна заплата (базата по договора)', f.salary, { size: 's' }),
      field('Година', f.year, { size: 's' }), field('Месец', f.month, { size: 's' })),
    row(field('Извънредни часове', f.hours, { size: 's' }),
      hintify(field('Кога е положен трудът', f.dayType),
        'чл. 262, ал. 1 КТ', 'Увеличение поне 50 % в работни дни, 75 % в почивни, 100 % на официални празници и 50 % при СИРВ.')),
    row(field('Извънреден труд от началото на годината (ч.)', f.yearHours, { size: 'm' })),
    el('div', { class: 'field' }, el('label', { class: 'choice' }, f.hasKtd, el('span', { text: 'Има КТД по чл. 51б (таван 300 ч. годишно — иначе 150 ч.)' }))),
    result,
    warnBox,
    el('p', { style: 'font-size:12px;color:var(--muted);margin-top:8px', text: 'Лимити по чл. 146 КТ: 150 ч./година (300 с КТД), 30 ч. дневен / 20 ч. нощен в месец, 6/4 в седмица, 3/2 в два последователни работни дни. Часовата ставка е от заплатата и реалните работни дни на месеца.' })), {
    when: [
      'Служител е работил след края на работния ден или в събота — смятате увеличеното заплащане.',
      'Проверка дали годишният лимит (150 ч., 300 ч. с КТД) не е надхвърлен, преди да се разпореди нов извънреден труд.',
      'Работа на официален празник — двойната ставка по чл. 262, ал. 1, т. 3.',
      'СИРВ: часовете над нормата в края на периода се заплащат с +50 %.',
    ],
    quotes: [
      { ref: 'чл. 143, ал. 1 и 2 КТ (изм. ДВ, бр. 25/2001)', text: 'Извънреден е трудът, който се полага по разпореждане или със знанието и без противопоставянето на работодателя или на съответния ръководител от работника или служителя извън установеното за него работно време. Извънредният труд е забранен.' },
      { ref: 'чл. 146, ал. 1 и 2 КТ (изм. ДВ, бр. 107/2020)', text: 'Продължителността на извънредния труд през една календарна година за един работник или служител не може да надвишава 150 часа. С колективен трудов договор по чл. 51б може да се уговаря по-голяма продължителност… но не повече от 300 часа през една календарна година.' },
      { ref: 'чл. 262, ал. 1 КТ', text: 'Положеният извънреден труд се заплаща с увеличение, уговорено между работника или служителя и работодателя, но не по-малко от: 1. 50 на сто – за работа през работните дни; 2. 75 на сто – за работа през почивните дни; 3. 100 на сто – за работа през дните на официалните празници; 4. 50 на сто – за работа при сумирано изчисляване на работното време.' },
    ],
  });
}

// ---- Д3. Имуществена отговорност -------------------------------------------

export function buildLiabilityCalc() {
  const f = {
    damage: input({ type: 'number', step: '0.01', min: '0', value: '', placeholder: 'напр. 2500' }),
    gross: input({ type: 'number', step: '0.01', min: '0', value: '', placeholder: 'уговореното месечно БТВ' }),
    accountable: el('input', { type: 'checkbox' }),
    net: input({ type: 'number', step: '0.01', min: '0', value: '', placeholder: 'нетно месечно' }),
    kids: el('input', { type: 'checkbox' }),
    discovered: dateInput(''),
    caused: dateInput(''),
  };
  const result = el('div', { class: 'calc-result' });
  const notesBox = el('div', { style: 'margin-top:8px' });

  function recompute() {
    clear(result); clear(notesBox);
    const damage = Number(f.damage.value), gross = Number(f.gross.value);
    if (damage > 0 && gross > 0) {
      const lim = liabilityLimit({ damage, monthlyGross: gross, accountable: f.accountable.checked });
      result.appendChild(line('Таван на отговорността (чл. 206)', money(lim.cap, 'EUR')));
      result.appendChild(line('Дължимо от работника', money(lim.amount, 'EUR'), true));
      lim.notes.forEach((n) => notesBox.appendChild(noteRow(n)));
    }
    const net = Number(f.net.value);
    if (net > 0) {
      try {
        const g = garnishablePart({ netIncome: net, hasChildren: f.kids.checked, date: localTodayIso() });
        result.appendChild(line('Допустима МЕСЕЧНА удръжка (чл. 446 ГПК)', money(g.amount, 'EUR'), true));
        result.appendChild(line('МРЗ към днес', money(g.minWage, 'EUR')));
        g.notes.forEach((n) => notesBox.appendChild(noteRow(n)));
      } catch (e) { result.appendChild(el('div', { class: 'calc-hint-line', text: '⚠ ' + e.message })); }
    }
    const disc = f.discovered.dataset.iso, caus = f.caused.dataset.iso;
    if (okIso(disc) && okIso(caus)) {
      const d = orderDeadline({ discovered: disc, caused: caus });
      result.appendChild(line('Заповедта по чл. 210 — краен срок', bg(d.deadline), true));
      d.notes.forEach((n) => notesBox.appendChild(noteRow(n)));
    }
    if (!result.children.length) result.appendChild(el('div', { class: 'calc-line', text: 'Попълнете вредата и възнаграждението (и по желание — нетото и датите).' }));
  }
  Object.values(f).forEach((i) => { i.addEventListener('input', recompute); i.addEventListener('change', recompute); });
  recompute();

  return withLaw(el('div', { class: 'panel calc-panel' },
    el('h3', { class: 'calc-title', text: 'Имуществена отговорност на работника (чл. 206, 210 КТ)' }),
    row(hintify(field('Размер на вредата', f.damage, { size: 's' }),
      'чл. 206, ал. 1 КТ', 'Отговорността при небрежност е в размер на вредата, но не повече от уговореното месечно трудово възнаграждение (ръководител — до трикратното).'),
      field('Уговорено месечно БТВ', f.gross, { size: 's' })),
    el('div', { class: 'field' }, el('label', { class: 'choice' }, f.accountable, el('span', { text: 'Отчетническа дейност (таван 3 месечни възнаграждения)' }))),
    el('h4', { text: 'Месечна удръжка от заплатата', style: 'margin:10px 0 4px' }),
    row(hintify(field('Нетен месечен доход', f.net, { size: 's' }),
      'чл. 446, ал. 1 и 3 ГПК (ал. 3 — нова, ДВ, бр. 55/2025)', 'Удръжката е дробна част по скалата в ал. 1, върху дохода след данъци и осигуровки; на лицето винаги остава поне минималната работна заплата.'),
    ),
    el('div', { class: 'field' }, el('label', { class: 'choice' }, f.kids, el('span', { text: 'Издържа деца' }))),
    el('h4', { text: 'Срок за заповедта', style: 'margin:10px 0 4px' }),
    row(field('Вредата е открита на', f.discovered, { size: 's' }), field('Причинена на', f.caused, { size: 's' })),
    result,
    notesBox), {
    when: [
      'Служител е повредил техника/стока по небрежност — определяте тавана на отговорността и заповедта по чл. 210.',
      'МОЛ/касиер с липси при отчетническа дейност — таван до 3 месечни възнаграждения.',
      'Смятате колко може да се удържа месечно от заплатата, без да се наруши чл. 446 ГПК.',
      'Следите срока за издаване на заповедта (1 месец от откриването, до 1 година от причиняването).',
    ],
    quotes: [
      { ref: 'чл. 203, ал. 1 и 2 КТ', text: 'Работникът или служителят отговаря имуществено съобразно правилата на тази глава за вредата, която е причинил на работодателя по небрежност при или по повод изпълнението на трудовите си задължения. За вреда, която е причинена умишлено или в резултат на престъпление…, отговорността се определя от гражданския закон.' },
      { ref: 'чл. 206, ал. 1 и 2 КТ', text: 'За вреда, причинена на работодателя по небрежност…, работникът или служителят отговаря в размер на вредата, но не повече от уговореното месечно трудово възнаграждение. Когато вредата е причинена от ръководител…, отговорността е в размер на вредата, но не повече от трикратния размер на уговореното месечно трудово възнаграждение.' },
      { ref: 'чл. 210, ал. 2 КТ', text: 'Заповедта се издава в 1-месечен срок от откриването на вредата или от плащането на сумата на третото лице, но не по-късно от 1 година от причиняването ѝ, а когато вредата е причинена от ръководител или при извършване на отчетническа дейност – в 3-месечен срок от откриването ѝ, но не по-късно от 5 години от нейното причиняване.' },
      { ref: 'чл. 446, ал. 3 ГПК (нова — ДВ, бр. 55/2025)', text: 'Дробните части по ал. 1, т. 1 и 2 се изчисляват върху пълния размер на дохода на осъденото лице след приспадане на сумите по ал. 2, като във всички случаи за лицето трябва да остане сума не по-малка от минималната работна заплата.' },
    ],
  });
}
